import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { ConsultationForm, AssessmentForm, ContactForm } from '../components/LeadForms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Contact Hero
const ContactHero = () => {
  return (
    <section className="pt-[140px] pb-[120px] md:pt-[180px] bg-background" data-testid="contact-hero">
      <div className="arch-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest text-gold mb-4">Contact Us</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
              Let's Start<br />
              <span className="text-gold">Your Journey</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12">
              Ready to transform your real estate vision? Get in touch with our team of 
              experts. We're here to help you navigate every step of your project.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <a
                href="tel:+917977482624"
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                  <Phone className="w-5 h-5 text-gold group-hover:text-black transition-colors" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Phone</p>
                  <p className="text-white text-lg">+91 79774 82624</p>
                </div>
              </a>

              <a
                href="mailto:info@agastyaventures.com"
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 border border-gold flex items-center justify-center group-hover:bg-gold transition-colors">
                  <Mail className="w-5 h-5 text-gold group-hover:text-black transition-colors" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                  <p className="text-white text-lg">info@agastyaventures.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white/70" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Location</p>
                  <p className="text-white text-lg">Mumbai, Maharashtra, India</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white/70" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Hours</p>
                  <p className="text-white text-lg">Mon - Sat: 10:00 AM - 7:00 PM</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <div className="mt-12">
              <a
                href="https://wa.me/917977482624?text=Hello%2C%20I'm%20interested%20in%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 hover:bg-[#20BD5A] transition-colors"
                data-testid="whatsapp-button"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="font-medium">Chat on WhatsApp</span>
              </a>
            </div>
          </motion.div>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Tabs defaultValue="consultation" className="w-full">
              <TabsList className="w-full grid grid-cols-3 bg-card border border-white/10 rounded-none h-auto p-0">
                <TabsTrigger
                  value="consultation"
                  className="rounded-none py-4 text-xs uppercase tracking-widest data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-consultation"
                >
                  Consultation
                </TabsTrigger>
                <TabsTrigger
                  value="assessment"
                  className="rounded-none py-4 text-xs uppercase tracking-widest data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-assessment"
                >
                  Assessment
                </TabsTrigger>
                <TabsTrigger
                  value="contact"
                  className="rounded-none py-4 text-xs uppercase tracking-widest data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-contact"
                >
                  Message
                </TabsTrigger>
              </TabsList>
              <TabsContent value="consultation" className="mt-0">
                <ConsultationForm />
              </TabsContent>
              <TabsContent value="assessment" className="mt-0">
                <AssessmentForm />
              </TabsContent>
              <TabsContent value="contact" className="mt-0">
                <ContactForm />
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Summary
const ServicesSummary = () => {
  const services = [
    'Architectural Design',
    'Interior Design',
    'Project Management Consulting',
    'Liaisoning',
    'Construction',
    'Finance Solutions',
    'Redevelopment Advisory'
  ];

  return (
    <section className="arch-section bg-card" data-testid="services-summary">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">How We Can Help</p>
          <h2 className="text-3xl md:text-4xl font-serif text-white">Our Services</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {services.map((service, index) => (
            <motion.div
              key={service}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="p-4 border border-white/10 text-center hover:border-gold/30 transition-colors"
            >
              <p className="text-white text-sm">{service}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Contact Page
const ContactPage = () => {
  return (
    <main data-testid="contact-page">
      <ContactHero />
      <ServicesSummary />
    </main>
  );
};

export default ContactPage;
